import React, { useEffect, useRef, useState } from 'react';
import { Search, Settings, GraduationCap, Layers, Monitor, Gauge, Scissors, Shield, Zap } from 'lucide-react';
import { strengthFramework } from '../../data/mock';

const iconMap = {
  Search,
  Settings,
  GraduationCap,
  Layers,
  Monitor,
  Gauge,
  Scissors,
  Shield
};

const StrengthFrameworkSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="framework"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'white' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <Zap size={16} />
            <span className="font-semibold text-sm">Our Methodology</span>
          </div>
          
          <h2 
            className={`heading-1 mb-2 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {strengthFramework.title}
          </h2>
          <p 
            className={`display-small mb-4 ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--brand-dark)' }}
          >
            {strengthFramework.subtitle}
          </p>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {strengthFramework.description}
          </p>
        </div>

        {/* Framework Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
          {strengthFramework.steps.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <div 
                key={index}
                className={`card p-6 group cursor-default ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
                style={{ animationDelay: `${(index + 4) * 80}ms` }}
              >
                {/* Letter Badge */}
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ background: 'var(--brand-primary)' }}
                >
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: 'var(--brand-dark)' }}
                  >
                    {step.letter}
                  </span>
                </div>

                {/* Icon & Name */}
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={20} style={{ color: 'var(--brand-dark)' }} />
                  <h3 className="heading-3" style={{ fontSize: '1.15rem' }}>
                    {step.name}
                  </h3>
                </div>

                {/* Description */}
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Closing Statement */}
        <div className={`text-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '800ms' }}>
          <p 
            className="heading-3 inline-block px-6 py-3 rounded-full"
            style={{ background: 'rgba(0, 69, 52, 0.08)', color: 'var(--brand-dark)' }}
          >
            {strengthFramework.closing}
          </p>
        </div>
      </div>
    </section>
  );
};

export default StrengthFrameworkSection;
